import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import {
  buildCsvUrl,
  formatCredits,
  formatDate,
  formatDuration,
  formatUsd,
  getConversation,
} from "@/lib/api";

function Stat({ label, value, mono = true }) {
  return (
    <div className="flex flex-col gap-1 border-l border-black/10 pl-4 py-2 min-w-0">
      <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45">
        {label}
      </span>
      <span
        className={`text-sm text-black truncate ${mono ? "font-mono-plex" : "font-body"}`}
        title={value == null ? "" : String(value)}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center border border-black/20 bg-black/[0.02] px-2 py-0.5 font-mono-plex text-[10px] uppercase tracking-[0.15em] text-black/80">
      {children}
    </span>
  );
}

function Message({ msg, idx }) {
  const role = (msg.role || "unknown").toLowerCase();
  const isUser = role === "user";
  const text = msg.message || msg.text || "";
  const time = msg.time_in_call_secs;
  return (
    <div
      className={`pl-4 py-2 border-l-2 ${isUser ? "border-black/50" : "border-[#007AFF]"}`}
      data-testid={`message-${idx}`}
    >
      <div className="flex items-center gap-3 mb-1">
        <span className="font-mono-plex text-[9px] uppercase tracking-[0.2em] text-black/55">
          {role}
        </span>
        {typeof time === "number" && (
          <span className="font-mono-plex text-[9px] text-black/35">
            @{formatDuration(time)}
          </span>
        )}
      </div>
      <p className="text-sm text-black/90 leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export default function DetailDrawer({ open, onOpenChange, conversationSummary }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !conversationSummary?.external_id) {
      setDetail(null);
      return;
    }
    setLoading(true);
    getConversation(conversationSummary.external_id)
      .then(setDetail)
      .catch(() => toast.error("Failed to load conversation"))
      .finally(() => setLoading(false));
  }, [open, conversationSummary?.external_id]);

  const c = detail || conversationSummary || {};
  const meta = c.raw_payload?.metadata || {};
  const displayTitle = c.call_summary_title || c.title || c.agent_name || "Untitled";
  const tools = Array.isArray(c.tool_names) ? c.tool_names : [];
  const sentiment = c.sentiment_analysis;
  const sentimentText = sentiment
    ? typeof sentiment === "string"
      ? sentiment
      : sentiment.overall_sentiment ||
        sentiment.overall ||
        JSON.stringify(sentiment).slice(0, 120)
    : null;

  const onExport = () => {
    if (!c.external_id) return;
    window.location.href = buildCsvUrl({ ids: c.external_id });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="conversation-drawer"
        className="w-full sm:max-w-[46vw] sm:min-w-[560px] bg-white border-l border-black/15 text-black p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-black/45 mb-2 truncate">
                Conversation · {c.external_id || "—"}
              </div>
              <SheetTitle
                className="font-heading text-2xl font-light tracking-tight text-black break-words"
                data-testid="drawer-title"
              >
                {displayTitle}
              </SheetTitle>
              <div className="mt-2 font-mono-plex text-xs text-black/65">
                {formatDate(c.date)} · {formatDuration(c.duration)} · {c.agent_name || "—"}
              </div>
            </div>
            <Button
              data-testid="export-single-button"
              variant="outline"
              className="h-9 rounded-none border-black/25 bg-white text-black hover:bg-black/5 hover:text-black font-mono-plex text-[11px] uppercase tracking-[0.15em]"
              onClick={onExport}
            >
              <Download className="w-3.5 h-3.5 mr-2" />
              CSV
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 grid grid-cols-2 gap-x-2 gap-y-3 border-b border-black/10" data-testid="metadata-grid">
            <Stat label="Duration" value={formatDuration(c.duration)} />
            <Stat label="Messages" value={c.messages_count ?? 0} />
            <Stat label="Credits used" value={formatCredits(c.credits_used ?? c.conversation_cost)} />
            <Stat label="LLM cost (USD)" value={formatUsd(c.llm_cost_usd)} />
            <Stat label="Platform cost (USD)" value={formatUsd(c.platform_cost_usd)} />
            <Stat label="Total cost (USD)" value={formatUsd(c.total_cost_usd)} />
            <Stat label="LLM $ / min" value={formatUsd(c.llm_cost_usd_per_min)} />
            <Stat label="Environment" value={c.environment || "—"} />
            <Stat label="Status" value={c.status || "—"} />
            <Stat label="Result" value={c.call_successful || "—"} />
            <Stat label="Score" value={c.call_success_score ?? "—"} />
            <Stat label="Rating" value={c.rating ?? "—"} />
            <Stat label="Direction" value={c.direction || "—"} />
            <Stat label="Language" value={c.main_language || "—"} />
            <Stat label="Source" value={c.conversation_initiation_source || "—"} />
            <Stat label="Termination" value={c.termination_reason || "—"} mono={false} />
            <Stat label="Agent" value={c.agent_name || "—"} mono={false} />
            <Stat label="Agent ID" value={c.agent_id || "—"} />
            <Stat label="Text only" value={c.text_only ? "yes" : "no"} />
            <Stat label="From #" value={c.phone_call_from || "—"} />
            <Stat label="To #" value={c.phone_call_to || "—"} />
            <Stat label="Sentiment" value={sentimentText || "—"} mono={false} />
          </div>

          {(c.call_summary_title || c.transcript_summary) && (
            <div className="px-6 py-4 border-b border-black/10 space-y-3" data-testid="summary-section">
              {c.call_summary_title && (
                <div>
                  <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 mb-1">
                    Call summary title
                  </div>
                  <p className="text-sm text-black/90">{c.call_summary_title}</p>
                </div>
              )}
              {c.transcript_summary && (
                <div>
                  <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 mb-1">
                    Transcript summary
                  </div>
                  <p className="text-sm text-black/80 leading-relaxed whitespace-pre-wrap">
                    {c.transcript_summary}
                  </p>
                </div>
              )}
            </div>
          )}

          {tools.length > 0 && (
            <div className="px-6 py-4 border-b border-black/10" data-testid="tools-section">
              <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 mb-2">
                Tools used
              </div>
              <div className="flex flex-wrap gap-2">
                {tools.map((t, i) => (
                  <Chip key={i}>{t}</Chip>
                ))}
              </div>
            </div>
          )}

          {meta && Object.keys(meta).length > 0 && (
            <details className="border-b border-black/10" data-testid="raw-metadata-section">
              <summary className="cursor-pointer list-none px-6 py-3 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-black/60 hover:text-black select-none">
                Raw metadata
              </summary>
              <pre className="px-6 pb-5 text-[11px] font-mono-plex text-black/70 overflow-auto max-h-64 bg-black/[0.03] border-t border-black/5">
                {JSON.stringify(meta, null, 2)}
              </pre>
            </details>
          )}

          <div className="px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-black/75">
                Transcript
              </h4>
              <span className="font-mono-plex text-[10px] text-black/45">
                {(c.raw_messages || []).length > 0
                  ? `${(c.raw_messages || []).length} messages`
                  : c.transcript
                    ? "archived · plain text"
                    : ""}
              </span>
            </div>
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-5 h-5 animate-spin inline-block text-black/40" />
              </div>
            ) : (c.raw_messages || []).length > 0 ? (
              <div className="flex flex-col gap-3" data-testid="transcript-container">
                {(c.raw_messages || []).map((m, i) => (
                  <Message msg={m} key={i} idx={i} />
                ))}
              </div>
            ) : c.transcript ? (
              <pre
                data-testid="transcript-plain"
                className="whitespace-pre-wrap text-sm text-black/85 font-body leading-relaxed bg-black/[0.02] border border-black/10 p-4"
              >
                {c.transcript}
              </pre>
            ) : (
              <p className="font-mono-plex text-xs text-black/45 uppercase tracking-[0.15em]">
                No transcript available
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
